import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DOMAIN_CONFIG } from '@fy-web-2/core';
import { DomainInfo, TenantApiService, ThemeConfig } from '@fy-web-2/data-access-tenant';
import { DEFAULT_THEME, Theme, applyTheme, resetTheme } from '@fy-web-2/shared-util';
import { firstValueFrom } from 'rxjs';
import { TenantSnapshot, TenantStore } from './tenant.store';

const STORAGE_KEY = 'fy-web-2.tenantInfo';

interface PersistedTenant {
  readonly host: string;
  readonly snapshot: Omit<TenantSnapshot, 'status' | 'error'>;
}

/**
 * Facade for tenant theming + branding. The orchestrator that:
 *   1. Resolves the tenant hostname (matching `domainInterceptor` rules).
 *   2. Calls `TenantApiService.getDomainData(host)`.
 *   3. Materialises a `Theme` and brand metadata, persists them to
 *      `localStorage` (keyed by host so multi-tenant browsing is safe).
 *   4. Applies the theme to `<html>` via the pure `applyTheme()` utility,
 *      sets the favicon, and updates the `<title>`.
 *
 * Components only read from this facade — never from `TenantStore` or
 * `TenantApiService` directly.
 */
@Injectable({ providedIn: 'root' })
export class TenantFacade {
  private readonly api = inject(TenantApiService);
  private readonly store = inject(TenantStore);
  private readonly document = inject(DOCUMENT);
  private readonly domainConfig = inject(DOMAIN_CONFIG);

  readonly status = this.store.status;
  readonly theme = this.store.theme;
  readonly logoUrl = this.store.logoUrl;
  readonly brandName = this.store.brandName;
  readonly faviconUrl = this.store.faviconUrl;
  readonly loginBackgroundUrl = this.store.loginBackgroundUrl;
  readonly isReady = this.store.isReady;

  /**
   * Synchronously rehydrates from `localStorage` so the *first paint* of the
   * app is already branded — no flash of unstyled content. Idempotent.
   * Wired into `APP_INITIALIZER` via `provideTenantBootstrap()` (in core).
   */
  bootstrap(): void {
    const persisted = this.readPersisted();
    const expectedHost = this.resolveHost();
    if (!persisted || persisted.host !== expectedHost) return;
    this.commit(persisted.snapshot);
  }

  /**
   * Fetches `domain-data` for the current host and applies the result. Safe
   * to call multiple times — already-loading calls coalesce; failures fall
   * back to the default theme without throwing.
   *
   * @returns the resolved `Theme` (default on failure) — handy for
   *   awaiting in tests / login init.
   */
  async loadDomainData(): Promise<Theme> {
    if (this.store.status() === 'loading') return this.theme() ?? DEFAULT_THEME;

    const host = this.resolveHost();
    this.store.setLoading();
    try {
      const info = await firstValueFrom(this.api.getDomainData(host));
      const snapshot = this.toSnapshot(info);
      this.commit(snapshot);
      this.writePersisted({ host, snapshot });
      return snapshot.theme ?? DEFAULT_THEME;
    } catch (err) {
      this.store.setError(this.toErrorMessage(err));
      // Fallback so the UI is always usable even when the API is down.
      applyTheme(DEFAULT_THEME, this.document);
      return DEFAULT_THEME;
    }
  }

  /** Wipe in-memory + persisted tenant state and revert the document theme. */
  clear(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage may be unavailable; ignore */
    }
    resetTheme(this.document);
    this.store.reset();
  }

  // ── Internals ───────────────────────────────────────────────────────────

  private commit(snapshot: Omit<TenantSnapshot, 'status' | 'error'>): void {
    this.store.setReady(snapshot);
    if (snapshot.theme) applyTheme(snapshot.theme, this.document);
    this.updateFavicon(snapshot.faviconUrl);
    this.updateDocumentTitle(snapshot.brandName);
  }

  private toSnapshot(info: DomainInfo): Omit<TenantSnapshot, 'status' | 'error'> {
    return {
      theme: this.toTheme(info.THEME_CONFIG),
      logoUrl: nonEmpty(info.LOGO),
      brandName: nonEmpty(info.domain_title),
      faviconUrl: nonEmpty(info.FAVICON),
      loginBackgroundUrl: nonEmpty(info.LOGIN_BACKGROUND_IMAGE_URL),
      raw: info,
    };
  }

  private toTheme(themeConfig: ThemeConfig | undefined): Theme {
    if (!themeConfig) return DEFAULT_THEME;
    return {
      primaryColor: themeConfig.primary_color ?? DEFAULT_THEME.primaryColor,
      secondaryColor: themeConfig.secondary_color ?? DEFAULT_THEME.secondaryColor,
      darkMode: false,//!!themeConfig.dark_mode,
    };
  }

  private resolveHost(): string {
    const win = (this.document.defaultView ?? null) as (Window & typeof globalThis) | null;
    const host = win?.location?.hostname ?? '';
    const isLocalLike = host === 'localhost' || host === '127.0.0.1' || host.startsWith('192.168.');
    return isLocalLike ? this.domainConfig.localHostName : host;
  }

  private updateFavicon(url: string | null): void {
    if (!url) return;
    const link = this.document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (link) link.href = url;
  }

  private updateDocumentTitle(brandName: string | null): void {
    if (!brandName) return;
    this.document.title = brandName;
  }

  private readPersisted(): PersistedTenant | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as PersistedTenant;
    } catch {
      return null;
    }
  }

  private writePersisted(persisted: PersistedTenant): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(persisted));
    } catch {
      /* storage may be unavailable (private mode); session-only fallback */
    }
  }

  private toErrorMessage(err: unknown): string {
    if (err instanceof HttpErrorResponse) {
      return `Tenant API failed (${err.status})`;
    }
    if (err instanceof Error) return err.message;
    return 'Unable to load tenant configuration.';
  }
}

function nonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}
